import { create } from 'zustand';
import { SwitchService } from '../services/switchService';
import { storage } from '../lib/storage';
import type { 
  User, 
  Service, 
  Deal, 
  Switch, 
  Notification, 
  LoadingState,
  Document,
  DocumentCategory 
} from '../types';
import * as api from '../lib/api';

type State = {
  user: User | null;
  services: Service[];
  deals: Deal[];
  switches: Switch[];
  notifications: Notification[];
  isAuthenticated: boolean;
  totalSavings: number;
  totalSwitches: number;
  loadingStates: LoadingState[];
  documents: Document[];
  error: string | null;
  
  // Auth actions
  register: (data: { email: string; password: string; name: string }) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  
  // Other actions...
  addService: (service: Omit<Service, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  updateService: (id: string, service: Partial<Service>) => Promise<void>;
  deleteService: (id: string) => Promise<void>;
  addDeal: (deal: Omit<Deal, 'id' | 'createdAt'>) => Promise<void>;
  updateDeal: (id: string, deal: Partial<Deal>) => Promise<void>;
  deleteDeal: (id: string) => Promise<void>;
  approveDeal: (dealId: string, serviceId?: string) => Promise<void>;
  markNotificationAsRead: (id: string) => Promise<void>;
  calculateTotalSavings: () => number;
  setLoadingState: (state: LoadingState) => void;
  clearError: () => void;
  
  // Document actions
  uploadDocument: (
    file: File,
    category: DocumentCategory,
    metadata: Partial<Document>
  ) => Promise<void>;
  updateDocument: (
    id: string,
    updates: Partial<Document>,
    newFile?: File
  ) => Promise<void>;
  deleteDocument: (id: string, fileUrl: string) => Promise<void>;
  loadDocuments: () => Promise<void>;
};

export const useStore = create<State>((set, get) => ({
  user: null,
  services: [],
  deals: [],
  switches: [],
  notifications: [],
  isAuthenticated: false,
  totalSavings: 0,
  totalSwitches: 0,
  loadingStates: [],
  documents: [],
  error: null,

  register: async (data) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { type: 'auth', status: true }],
        error: null
      }));

      const { data: authData, error: signUpError } = await api.signUp({
        email: data.email,
        password: data.password,
        options: {
          data: {
            name: data.name
          }
        }
      });

      if (signUpError) throw signUpError;

      // Auto-login after successful registration
      const { data: signInData, error: signInError } = await api.signIn(data.email, data.password);
      if (signInError) throw signInError;

      // Create user profile in the users table
      if (signInData.user) {
        await api.createUserProfile(signInData.user.id, data.email, data.name);
      }

      const user = await api.getCurrentUser();
      
      set({ 
        isAuthenticated: true,
        user,
        error: null
      });

    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error && typeof error === 'object' && 'code' in error) {
        if (error.code === 'user_already_exists') {
          errorMessage = 'This email is already registered. Please try logging in instead.';
        } else if (error.message) {
          errorMessage = error.message;
        }
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      set({ error: errorMessage });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => s.type !== 'auth')
      }));
    }
  },

  login: async (email, password) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { type: 'auth', status: true }],
        error: null
      }));

      const { error: signInError } = await api.signIn(email, password);
      if (signInError) throw signInError;

      const user = await api.getCurrentUser();
      
      // Load cached data
      const cachedServices = await storage.getItem<Service[]>('services');
      const cachedDeals = await storage.getItem<Deal[]>('deals');
      
      set({ 
        isAuthenticated: true,
        user,
        services: cachedServices || [],
        deals: cachedDeals || [],
        error: null
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Authentication failed' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => s.type !== 'auth')
      }));
    }
  },

  logout: async () => {
    try {
      const { error } = await api.signOut();
      if (error) throw error;

      set({ 
        isAuthenticated: false,
        user: null,
        services: [],
        deals: [],
        switches: [],
        notifications: [],
        documents: [],
        error: null
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Logout failed' });
      throw error;
    }
  },

  setLoadingState: (newState) => {
    set(state => ({
      loadingStates: [
        ...state.loadingStates.filter(s => 
          s.type !== newState.type || s.id !== newState.id
        ),
        newState
      ]
    }));
  },

  clearError: () => {
    set({ error: null });
  },

  addService: async (serviceData) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { type: 'service', status: true }],
        error: null
      }));

      const newService = await api.createService(serviceData);
      
      set(state => {
        const updatedServices = [...state.services, newService];
        storage.setItem('services', updatedServices);
        return { services: updatedServices };
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add service' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => s.type !== 'service')
      }));
    }
  },

  updateService: async (id, serviceData) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'service',
          id,
          status: true 
        }],
        error: null
      }));

      const updatedService = await api.updateService(id, serviceData);
      
      set(state => {
        const updatedServices = state.services.map(service => 
          service.id === id ? updatedService : service
        );
        storage.setItem('services', updatedServices);
        return { services: updatedServices };
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update service' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'service' && s.id === id)
        )
      }));
    }
  },

  deleteService: async (id) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'service',
          id,
          status: true 
        }],
        error: null
      }));

      await api.deleteService(id);
      
      set(state => {
        const updatedServices = state.services.filter(service => service.id !== id);
        storage.setItem('services', updatedServices);
        return { services: updatedServices };
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete service' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'service' && s.id === id)
        )
      }));
    }
  },

  addDeal: async (dealData) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { type: 'deal', status: true }],
        error: null
      }));

      const newDeal = await api.createDeal(dealData);
      
      set(state => {
        const updatedDeals = [...state.deals, newDeal];
        storage.setItem('deals', updatedDeals);
        return { deals: updatedDeals };
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to add deal' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => s.type !== 'deal')
      }));
    }
  },

  updateDeal: async (id, dealData) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'deal',
          id,
          status: true 
        }],
        error: null
      }));

      const updatedDeal = await api.updateDeal(id, dealData);
      
      set(state => {
        const updatedDeals = state.deals.map(deal => 
          deal.id === id ? updatedDeal : deal
        );
        storage.setItem('deals', updatedDeals);
        return { deals: updatedDeals };
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to update deal' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'deal' && s.id === id)
        )
      }));
    }
  },

  deleteDeal: async (id) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'deal',
          id,
          status: true 
        }],
        error: null
      }));

      await api.deleteDeal(id);
      
      set(state => {
        const updatedDeals = state.deals.filter(deal => deal.id !== id);
        storage.setItem('deals', updatedDeals);
        return { deals: updatedDeals };
      });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete deal' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'deal' && s.id === id)
        )
      }));
    }
  },

  approveDeal: async (dealId, serviceId) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'switch', 
          id: dealId, 
          status: true 
        }],
        error: null
      }));

      const deal = get().deals.find(d => d.id === dealId);
      if (!deal) throw new Error('Deal not found');

      const switchData = await api.createSwitch(dealId, serviceId || '', deal.savings * 12);
      const switchService = new SwitchService(switchData.id);
      await switchService.processSwitch();

      set(state => ({
        switches: [...state.switches, { ...switchData, status: 'completed' }],
        totalSwitches: state.totalSwitches + 1,
        totalSavings: state.totalSavings + switchData.savingsAmount
      }));

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Switch failed' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'switch' && s.id === dealId)
        )
      }));
    }
  },

  markNotificationAsRead: async (id) => {
    try {
      set({ error: null });
      await api.markNotificationAsRead(id);
      
      set(state => ({
        notifications: state.notifications.map(notification => 
          notification.id === id 
            ? { ...notification, read: true } 
            : notification
        )
      }));

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to mark notification as read' });
      throw error;
    }
  },

  calculateTotalSavings: () => {
    const { switches } = get();
    const completedSwitches = switches.filter(s => s.status === 'completed');
    return completedSwitches.reduce((total, s) => total + s.savingsAmount, 0);
  },

  uploadDocument: async (file, category, metadata) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'document', 
          status: true 
        }],
        error: null
      }));

      const document = await api.uploadDocument(file, category, metadata);
      
      set(state => ({
        documents: [...state.documents, document]
      }));

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Document upload failed' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          s.type !== 'document'
        )
      }));
    }
  },

  updateDocument: async (id, updates, newFile) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'document',
          id,
          status: true 
        }],
        error: null
      }));

      const updatedDocument = await api.updateDocument(id, updates, newFile);
      
      set(state => ({
        documents: state.documents.map(doc =>
          doc.id === id ? updatedDocument : doc
        )
      }));

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Document update failed' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'document' && s.id === id)
        )
      }));
    }
  },

  deleteDocument: async (id, fileUrl) => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'document',
          id,
          status: true 
        }],
        error: null
      }));

      await api.deleteDocument(id, fileUrl);
      
      set(state => ({
        documents: state.documents.filter(doc => doc.id !== id)
      }));

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Document deletion failed' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          !(s.type === 'document' && s.id === id)
        )
      }));
    }
  },

  loadDocuments: async () => {
    try {
      set(state => ({
        loadingStates: [...state.loadingStates, { 
          type: 'document', 
          status: true 
        }],
        error: null
      }));

      const documents = await api.getDocuments();
      set({ documents });

    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load documents' });
      throw error;
    } finally {
      set(state => ({
        loadingStates: state.loadingStates.filter(s => 
          s.type !== 'document'
        )
      }));
    }
  },
}));